import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Fade-in variants (desktop-only ≥992px)
 * - Skips elements inside [data-animate="parallax"] so parallax can own that lifecycle.
 */
(function () {
    const SELECTOR = [
        '[data-animate="fadeIn"]',
        '[data-animate="fadeInUp"]',
        '[data-animate="fadeInUpDesktop"]'
    ].join(', ');

    // Respect reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        document.querySelectorAll(SELECTOR).forEach((el) => {
            el.style.opacity = '1';
            el.style.visibility = 'visible';
            el.style.transform = 'none';
        });
        return;
    }

    if (ScrollTrigger.saveStyles) {
        ScrollTrigger.saveStyles([SELECTOR]);
    }

    const mm = gsap.matchMedia ? gsap.matchMedia() : ScrollTrigger.matchMedia();

    mm.add('(min-width: 992px)', () => {
        const els = document.querySelectorAll(SELECTOR);
        if (!els.length) return;

        els.forEach((el) => {
            // If this element is inside a parallax-pinned block, let parallax control it.
            if (el.closest('[data-animate="parallax"]')) return;

            const type = el.getAttribute('data-animate');
            const isUp = type === 'fadeInUp' || type === 'fadeInUpDesktop';

            gsap.set(el, isUp
                ? { autoAlpha: 0, y: 24, willChange: 'opacity, transform' }
                : { autoAlpha: 0, willChange: 'opacity' }
            );

            ScrollTrigger.create({
                trigger: el,
                start: 'top 80%',
                once: false,
                invalidateOnRefresh: true,
                onEnter: () => {
                    gsap.to(el, {
                        autoAlpha: 1,
                        y: isUp ? 0 : undefined,
                        duration: 2.0,
                        ease: 'power2.out',
                        overwrite: 'auto'
                    });
                },
                onEnterBack: () => {
                    gsap.to(el, {
                        autoAlpha: 1,
                        y: isUp ? 0 : undefined,
                        duration: 1.6,
                        ease: 'power2.out',
                        overwrite: 'auto'
                    });
                }
            });
        });

        window.addEventListener('load', () => ScrollTrigger.refresh());
        return () => {};
    });
})();

// Parallax pinned section
(function () {
    const SELECTOR = '[data-animate="parallax"]';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (ScrollTrigger.saveStyles) {
        ScrollTrigger.saveStyles([
            SELECTOR,
            `${SELECTOR} [data-parallax-target]`,
            `${SELECTOR} [data-parallax-body]`
        ]);
    }

    const mm = gsap.matchMedia ? gsap.matchMedia() : ScrollTrigger.matchMedia();

    mm.add('(min-width: 992px)', () => {
        const parallaxEls = document.querySelectorAll(SELECTOR);
        if (!parallaxEls.length) return;

        parallaxEls.forEach((el) => {
            el.style.minHeight = el.style.minHeight || '100vh';
            el.style.position = el.style.position || 'relative';
            if (!el.style.zIndex) el.style.zIndex = '0';

            const target =
                el.querySelector('[data-parallax-target]') ||
                el.querySelector('.image-wrap') ||
                el.querySelector('.block-image');

            const body = el.querySelector('[data-parallax-body]') || el.querySelector('.block-body');

            // Intro target: animate this on load/enter so we don't fight the scrubbed y on .block-body
            const bodyIntro = body ? (body.querySelector('.block-content') || body) : null;

            if (reducedMotion) {
                if (target) target.style.transform = 'none';
                if (body) body.style.transform = 'none';
                if (bodyIntro) {
                    bodyIntro.style.opacity = '1';
                    bodyIntro.style.visibility = 'visible';
                    bodyIntro.style.transform = 'none';
                }
                return;
            }

            // Tuning Knobs
            const BG_Y_PERCENT = -22;
            const PIN_DISTANCE = '+=150%';

            // Block body animation (viewport-based)
            const BODY_VH_TRAVEL = 70;     // 55–85
            const BODY_SPEED = 1.6;       // 1.1–1.8
            // --------------------

            const getBodyTravelPx = () => {
                const vh = window.innerHeight || 0;
                return (vh * (BODY_VH_TRAVEL / 100)) * BODY_SPEED;
            };

            // Initial states
            if (body) {
                gsap.set(body, {
                    y: 0,
                    willChange: 'transform'
                });
            }

            // Intro animation (fade + up)
            if (bodyIntro) {
                gsap.set(bodyIntro, {
                    autoAlpha: 0,
                    y: 24,
                    willChange: 'opacity, transform'
                });
            }

            const introIn = () => {
                if (!bodyIntro) return;
                gsap.to(bodyIntro, {
                    autoAlpha: 1,
                    y: 0,
                    duration: 1.0,
                    ease: 'power2.out',
                    overwrite: 'auto'
                });
            };

            const introReset = () => {
                if (!bodyIntro) return;
                gsap.set(bodyIntro, { autoAlpha: 0, y: 24 });
            };

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: el,
                    start: 'top top',
                    end: PIN_DISTANCE,
                    scrub: true,
                    pin: true,
                    pinSpacing: false,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                    onEnter: introIn,
                    onEnterBack: introIn,
                    onLeaveBack: introReset
                }
            });

            if (target) {
                tl.to(target, { yPercent: BG_Y_PERCENT, ease: 'none' }, 0);
            }

            // Parallax the BODY itself (scrub-controlled)
            if (body) {
                tl.to(body, { y: () => -getBodyTravelPx(), ease: 'none' }, 0);
            }

            // Ensure visible on initial load at the very top
            requestAnimationFrame(() => {
                const st = tl.scrollTrigger;
                if (!st) return;
                if (window.scrollY <= st.start + 2) {
                    introIn();
                }
            });
        });

        window.addEventListener('load', () => ScrollTrigger.refresh());
        return () => {};
    });
})();
